import { useEffect, useState } from "react";

import { handlePaging } from "../../../../components/api/APIUtilities";
import { postNotification } from "../../../../components/utilities/Notifications";
import ListResponsive from "../../../../components/other/ListResponsive";
import {
  BCPSearch,
  BCPSquarePlus,
  BCPXmark,
} from "../../../../components/icons/bcp/BCPIcons";
import {
  Backdrop,
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";

import TeamCard from "../../../../components/layout/TeamCard";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import {
  getData,
  postData,
} from "../../../../components/utilities/UtilityFunctions";
import { isAppMode } from "../../../../global/AppOverlay";

export default function TeamsTab(props) {
  const { user, setUser, authContext } = props;
  const [processing, setProcessing] = useState(false);
  const [subLoading, setSubLoading] = useState(true);
  const [teams, setTeams] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const appMode = isAppMode();

  const sortTeams = (a, b) => {
    if (a.id === user.teamId) return -1;
    if (b.id === user.teamId) return 1;
    if (a.name && b.name) {
      var nameA = a.name.toLowerCase().trim();
      var nameB = b.name.toLowerCase().trim();
      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;
    } else if (a.name) {
      return 1;
    } else if (b.name) {
      return -1;
    }
    return 0;
  };

  const leaveTeam = async (team) => {
    if (
      team.ownerId &&
      team.ownerId === authContext?.authState?.identity?.userId
    ) {
      postNotification(
        "error",
        "You cannot leave a team where you are the owner"
      );
      return;
    }
    setProcessing(true);

    try {
      await postData(`/teams/${team.id}/removeMember`, {
        body: {
          userId: authContext?.authState?.identity?.userId,
        },
      });
      var newTeams = teams?.filter(function (el) {
        return el.id !== team.id;
      });
      setTeams(newTeams);
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
    return;
  };

  const addTeam = (team) => {
    var newTeams = [...teams];
    newTeams.push(team);
    newTeams.sort(sortTeams);
    setTeams(newTeams);
  };

  const setTeamAsDefault = async (team) => {
    setProcessing(true);

    try {
      var newUser = await postData(`/users/${user.id}`, {
        body: {
          set: {
            teamId: team.id,
          },
        },
      });
      setUser(newUser);
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
    return;
  };

  useEffect(() => {
    const check = async () => {
      setSubLoading(true);

      var apiTeams = await getData(`/users/${user?.id}/teams`);

      apiTeams.sort(sortTeams);

      setTeams(apiTeams);
      setSubLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [user]);

  return (
    <>
      <ListResponsive
        originalList={subLoading ? null : teams}
        doSearch
        doPages
        initialRowsPerPage={24}
        actionButton={[
          {
            props: {
              variant: "outlined",
              color: "primary",
              fullWidth: true,
              onClick: (e) => {
                setSearchOpen(true);
              },
              startIcon: <BCPSearch />,
            },
            label: "Join Team",
          },
          {
            props: {
              variant: "contained",
              color: "primary",
              fullWidth: true,
              href: "/organize/team",
              target: `${appMode ? "_blank" : "_self"}`,
              startIcon: <BCPSquarePlus />,
            },
            label: "New Team",
          },
        ]}
        searchProps={{
          label: "Search Teams",
          performSearch: (originalList, searchText) => {
            if (!searchText || searchText === "") {
              return originalList;
            } else {
              var filteredList = originalList?.filter(function (el) {
                if (el?.name?.toLowerCase().includes(searchText)) return true;

                return false;
              });
              return filteredList;
            }
          },
        }}
        rowProps={{
          mapFunction: (row, index) => (
            <Grid
              key={row?.id || index}
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ p: 1 }}
            >
              <TeamCard
                organize
                team={row}
                authContext={authContext}
                key={row?.id || index}
                highlight={row.id === user.teamId}
                removeMember={async () => {
                  await leaveTeam(row);
                }}
                setTeamAsDefault={async () => {
                  await setTeamAsDefault(row);
                }}
              />
            </Grid>
          ),
        }}
      />
      {searchOpen && (
        <TeamSearchModal
          setProcessing={setProcessing}
          open={searchOpen}
          setOpen={setSearchOpen}
          currentTeams={teams}
          addTeam={addTeam}
          authContext={authContext}
        />
      )}
      {Boolean(processing) && <ModalLoadingFull />}
    </>
  );
}

function TeamSearchModal(props) {
  const { authContext, addTeam, currentTeams, setProcessing, open, setOpen } =
    props;
  const [searchName, setSearchName] = useState("");
  const [teams, setTeams] = useState([]);
  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [doSearch, setDoSearch] = useState(false);

  const searchWrapper = async (e) => {
    setHasMore(false);
    setNextKey(null);
    setTeams(null);
    setDoSearch(true);
  };

  const search = async () => {
    setDoSearch(false);
    if (searchName?.trim()?.length < 3) {
      postNotification(
        "warning",
        "Please enter a search name of at least 3 characters"
      );
      return;
    }
    setProcessing(true);
    let params = {
      searchName: searchName?.trim()?.toLowerCase(),
      "expand[]": ["owner"],
    };
    try {
      var _teams = await handlePaging("/teams", params, nextKey, setNextKey);

      _teams = _teams?.filter((el) => {
        return !currentTeams?.filter((_el) => {
          return _el.id === el.id;
        })?.length;
      });
      _teams.sort((a, b) => {
        if (a.name && b.name) {
          var nameA = a.name.toLowerCase().trim();
          var nameB = b.name.toLowerCase().trim();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
        } else if (a.name) {
          return 1;
        } else if (b.name) {
          return -1;
        }
        return 0;
      });

      setHasMore(_teams?.length === params.limit);
      setTeams(teams?.length ? [...teams, ..._teams] : _teams);
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };

  useEffect(() => {
    if (doSearch) {
      search();
    }
    // eslint-disable-next-line
  }, [doSearch]);

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: 9999 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          height: "85vh",
          maxHeight: "85vh",
          overflowY: "scroll",
          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => {
            setOpen(false);
          }}
        >
          <BCPXmark />
        </IconButton>
        <Grid
          container
          spacing={2}
          sx={{
            pb: 1,
            mb: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Grid item xs>
            <Typography variant="h4" component="h2">
              Team Search
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              label="Search"
              name="searchName"
              value={searchName}
              variant="outlined"
              fullWidth
              margin="normal"
              onBlur={async (e) => {
                setSearchName(e.target.value);
              }}
              onChange={(e) => {
                setSearchName(e.target.value);
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              type="submit"
              disabled={
                !searchName?.trim()?.length || searchName?.trim()?.length < 3
              }
              onClick={searchWrapper}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        <ListResponsive
          originalList={teams}
          loadMore={search}
          hasMore={hasMore}
          scrollToTop
          rowProps={{
            mapFunction: (row, index) => (
              <Grid key={row?.id || index} item xs={12} sx={{ pt: 2 }}>
                <Grid container>
                  <Grid item xs={12} sm={6}>
                    <Typography>{row?.name}</Typography>
                    {row?.owner?.id !== undefined ? (
                      <Typography variant="caption">
                        {`Owner: ${row?.owner?.firstName || ""} ${
                          row?.owner?.lastName || ""
                        }`.trim()}
                      </Typography>
                    ) : (
                      <></>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={async (e) => {
                        setProcessing(true);
                        var _team = await postData(
                          `/teams/${row.id}/addMember`,
                          {
                            body: {
                              userId: authContext?.authState?.identity?.userId,
                            },
                          }
                        );
                        addTeam(_team);
                        setProcessing(false);
                        setOpen(false);
                      }}
                    >
                      Join
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            ),
          }}
        />
      </Box>
    </Modal>
  );
}
